import set from 'lodash.set';

import {
  GENERATE_IMAGES,
  GenerateImagesAction,
  RESET_IMAGE_GENERATION,
  ResetImageGenerationAction,
  IMAGE_GENERATION_SUCCESS,
  IMAGE_GENERATION_FAILED,
  ImageGenerationSuccessAction,
  ImageGenerationFailedAction,
  REGENERATE_IMAGES,
  RegenerateImagesAction,
  SetImageGenerationInputsAction,
  SET_IMAGE_GENERATION_INPUTS,
  EDIT_IMAGE_PROMPT,
  EditImagePromptAction,
  CHANGE_AI_PANEL_STEP,
  ChangeAiPanelStepAction,
  SET_SCRIPT_GENERATION_INPUTS,
  SetScriptGenerationInputsAction,
  GENERATE_SCRIPT,
  GenerateScriptAction,
  RESET_SCRIPT_GENERATION,
  EDIT_GENERATED_SCRIPT,
  ResetScriptGenerationAction,
  GENERATE_SCRIPT_SUCCESS,
  GENERATE_SCRIPT_FAILED,
  GenerateScriptFailedAction,
  GenerateScriptSuccessAction,
  SetGenerationFeedbackAction,
  SET_GENERATION_FEEDBACK,
  SET_TAGS,
  SetTagsAction,
  SET_FEEDBACK_COMMENTS,
  SetFeedbackCommentsAction,
  EditGeneratedScriptAction,
  TryNewScriptPromptAction,
  TRY_NEW_SCRIPT_PROMPT
} from 'js/actionCreators/aiFeaturesActions';
import { SHOW_LEFT_HAND_PANEL, ShowLeftHandPanelAction } from 'js/actionCreators/uiActions';
import { SHOW_IMAGE_GENERATION_VIEW, ShowImageGenerationViewAction } from 'js/actionCreators/ImageDrawerActions';
import { AI_FEATURES } from 'js/config/config';
import {
  aiFeaturesState,
  AiFeaturesTabStep,
  AiImageGenerationTabStep,
  AiPanelTab,
  AiScriptGenerationTabStep,
  ImageGenerationStatus,
  LeftHandPanel
} from 'js/types';
import { EDITOR_CLOSED, EditorClosedAction } from 'js/actionCreators/editorActions';

type BetaAiFeaturesActionTypes =
  | GenerateImagesAction
  | ResetImageGenerationAction
  | ImageGenerationSuccessAction
  | ImageGenerationFailedAction
  | RegenerateImagesAction
  | SetImageGenerationInputsAction
  | ShowLeftHandPanelAction
  | ShowImageGenerationViewAction
  | EditImagePromptAction
  | ChangeAiPanelStepAction
  | SetScriptGenerationInputsAction
  | GenerateScriptAction
  | ResetScriptGenerationAction
  | GenerateScriptSuccessAction
  | GenerateScriptFailedAction
  | SetGenerationFeedbackAction
  | GenerateScriptFailedAction
  | SetTagsAction
  | SetFeedbackCommentsAction
  | SetImageGenerationInputsAction
  | EditGeneratedScriptAction
  | TryNewScriptPromptAction
  | EditorClosedAction;

const initialState: aiFeaturesState = {
  panelTab: AiPanelTab.FEATURES,
  tabState: {
    [AiPanelTab.FEATURES]: {
      step: AiFeaturesTabStep.OVERVIEW
    },
    [AiPanelTab.IMAGE_GENERATION]: {
      step: AiImageGenerationTabStep.OVERVIEW
    },
    [AiPanelTab.SCRIPT_GENERATION]: {
      step: AiScriptGenerationTabStep.OVERVIEW
    }
  },
  imageGenerationInputs: {
    style: 'COLOR',
    prompt: ''
  },
  imageGenerationStatus: ImageGenerationStatus.NOT_STARTED,
  imageGenerationResults: {
    images: [],
    error: null,
    id: null,
    enhancedPrompt: null
  },
  scriptGenerationInputs: {
    prompt: '',
    predefinedStyles: [],
    customStyle: '',
    lengthOfScriptMinutes: AI_FEATURES.SCRIPT.DEFAULT_SCRIPT_LENGTH_MINUTES,
    lengthOfScriptSeconds: 0
  },
  scriptGenerationLoading: false,
  scriptGenerationResults: {
    script: ''
  },
  feedbackRatings: {
    script: undefined
  },
  feedbackTags: {
    script: []
  },
  feedbackComments: {
    script: ''
  }
};

export default function aiFeaturesReducer(state = initialState, action: BetaAiFeaturesActionTypes): aiFeaturesState {
  switch (action.type) {
    case EDITOR_CLOSED: {
      return {
        ...initialState
      };
    }

    case CHANGE_AI_PANEL_STEP: {
      return {
        ...state,
        tabState: {
          ...state.tabState,
          [action.tab]: {
            step: action.step
          }
        }
      };
    }

    case SHOW_IMAGE_GENERATION_VIEW: {
      return {
        ...state,
        tabState: {
          ...state.tabState,
          [AiPanelTab.IMAGE_GENERATION]: {
            step: AiImageGenerationTabStep.OVERVIEW
          }
        },
        imageGenerationInputs: action.prompt
          ? { ...state.imageGenerationInputs, prompt: action.prompt }
          : state.imageGenerationInputs
      };
    }

    case SHOW_LEFT_HAND_PANEL: {
      const { name } = action;
      if (name !== LeftHandPanel.AI_GENERATION) return state;
      const { tab } = action;
      return {
        ...state,
        ...(tab && { panelTab: tab })
      };
    }

    case GENERATE_IMAGES: {
      return {
        ...state,
        imageGenerationStatus: ImageGenerationStatus.IN_PROGRESS,
        imageGenerationResults: initialState.imageGenerationResults
      };
    }

    case REGENERATE_IMAGES: {
      return {
        ...state,
        imageGenerationStatus: ImageGenerationStatus.IN_PROGRESS
      };
    }

    case IMAGE_GENERATION_SUCCESS: {
      return {
        ...state,
        imageGenerationStatus: ImageGenerationStatus.COMPLETE,
        imageGenerationResults: {
          images: action.images,
          error: null,
          id: action.imageGenerationId,
          enhancedPrompt: action.enhancedPrompt
        }
      };
    }

    case IMAGE_GENERATION_FAILED: {
      return {
        ...state,
        imageGenerationStatus: ImageGenerationStatus.FAILED,
        imageGenerationResults: {
          images: [],
          error: action.error,
          id: null,
          enhancedPrompt: null
        }
      };
    }

    case RESET_IMAGE_GENERATION: {
      return {
        ...state,
        imageGenerationInputs: initialState.imageGenerationInputs,
        imageGenerationStatus: initialState.imageGenerationStatus,
        imageGenerationResults: initialState.imageGenerationResults
      };
    }

    case EDIT_IMAGE_PROMPT: {
      return {
        ...state,
        imageGenerationStatus: ImageGenerationStatus.NOT_STARTED,
        imageGenerationResults: initialState.imageGenerationResults
      };
    }

    case SET_IMAGE_GENERATION_INPUTS: {
      return {
        ...state,
        imageGenerationInputs: {
          ...state.imageGenerationInputs,
          ...action
        }
      };
    }

    case SET_SCRIPT_GENERATION_INPUTS: {
      return {
        ...state,
        scriptGenerationInputs: {
          ...state.scriptGenerationInputs,
          ...action
        }
      };
    }

    case GENERATE_SCRIPT: {
      return {
        ...state,
        scriptGenerationLoading: true,
        scriptGenerationResults: {
          script: ''
        }
      };
    }

    case EDIT_GENERATED_SCRIPT: {
      return {
        ...state,
        scriptGenerationResults: {
          script: action.script
        }
      };
    }

    case RESET_SCRIPT_GENERATION: {
      return {
        ...state,
        scriptGenerationInputs: initialState.scriptGenerationInputs,
        scriptGenerationResults: {
          script: ''
        }
      };
    }

    case GENERATE_SCRIPT_SUCCESS: {
      return {
        ...state,
        scriptGenerationLoading: false,
        scriptGenerationResults: {
          script: action.script
        }
      };
    }

    case GENERATE_SCRIPT_FAILED: {
      return {
        ...state,
        scriptGenerationLoading: false
      };
    }

    case SET_GENERATION_FEEDBACK: {
      const newState = { ...state };
      set(newState, `feedbackRatings.${action.updatePath}`, action.feedback);
      return newState;
    }

    case SET_TAGS: {
      const newState = { ...state };
      set(newState, action.statePath, action.tags);
      return newState;
    }

    case SET_FEEDBACK_COMMENTS: {
      const newState = { ...state };
      set(newState, `feedbackComments.${action.updatePath}`, action.comments);
      return newState;
    }

    case TRY_NEW_SCRIPT_PROMPT: {
      return {
        ...state,
        feedbackRatings: {
          ...state.feedbackRatings,
          script: undefined
        },
        feedbackTags: {
          ...state.feedbackTags,
          script: []
        },
        feedbackComments: {
          ...state.feedbackComments,
          script: ''
        }
      };
    }

    default:
      return state;
  }
}
